import { ChangeEvent, FC, KeyboardEvent, memo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import CurrencyPrice from '../../../common/currency-price/currency-price';
import FontAwesomeIcon from '../../../ui/font-awesome-icon/font-awesome-icon';
import Routes from '../../../constants/routes';
import useIsMobile from '../../../hooks/use-is-mobile';
import translation from '../../../languages';
import { DetachCreditsResponse } from '../../../api/credits/detach-credits';
import qualityEvents from '../../../services/quality-events/quality-events';
import { removeVoucher } from '../../../api/vouchers/remove-voucher';
import { saveVoucher } from '../../../api/vouchers/save-voucher';
import cn from '../../../utils/classnames';
import toTitleCase from '../../../utils/material/to-title-case';
import formatPrice from '../../../utils/price/format-price';
import schoolAccount from '../../../utils/school-account';
import PaypalExpress from '../../payment/components/controls/paypal-express/paypal-express';
import { ErrorMessageType } from '../../payment/components/error-message/error-message';
import { VoucherError } from '../cart';
import PaymentLogos from '../payment-logos/payment-logos';
import CartActionBlock from './components/cart-action-block';
import MobileCheckoutBlock from './components/mobile-checkout-block/mobile-checkout-block';
import MobileVoucher from './components/mobile-voucher';
import Voucher from './components/voucher';
import './material-cart-summary.scss';

const ZERO_FINAL_PRICE = '0,00';

export type MaterialCartSummaryProps = TranslationProps & {
    cart: Cart;
    reset: (cart: Cart) => void;
    setVoucherError: (param: VoucherError) => void;
    showBlinder: (param: boolean) => void;
    onPaypalExpressError: (error: ErrorMessageType) => void;
};

const MaterialCartSummary: FC<MaterialCartSummaryProps> = memo((props) => {
    const { cart, showBlinder, reset, setVoucherError, onPaypalExpressError, t } = props;
    const isAppliedPromo = cart?.promo?.applied;
    const isAppliedPromoVoucher = isAppliedPromo && cart?.voucher?.code;
    const user = useSelector((state: RootState) => state.main.user);

    const [voucherCode, setVoucherCode] = useState<string>('');
    const isMobile = useIsMobile();

    const finalPrice = formatPrice(cart.finalPrice);
    const { usedCredits } = cart;
    const hundredPercentVoucher = Boolean(cart.voucher && finalPrice === '0,00' && !usedCredits);

    const resetCart = ({
        data: {
            items: { cart }
        }
    }: DetachCreditsResponse) => {
        reset(cart);

        showBlinder(false);
    };

    const goToPaymentPage = () => {
        const voucherApplied = Boolean(cart.voucher);

        if (voucherCode && !voucherApplied) {
            attachVoucher(defineRoute).then(() => {});
        } else {
            defineRoute();
        }
    };

    const defineRoute = () => {
        window.locationReload(Routes.PAYMENT.path);
    };

    const handleVoucherInputKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            attachVoucher().then(() => {});
        }
    };

    const handleVoucher = (e: ChangeEvent<HTMLInputElement>) => {
        setVoucherCode(e.target.value);
    };

    const attachVoucher = async (callback?: () => void) => {
        const lowerCaseCode = String(voucherCode).trim().toLowerCase();

        showBlinder(true);
        setVoucherError(null);

        if (lowerCaseCode === 'herbst232' || lowerCaseCode === 'octubre232') {
            const voucherCode = `${toTitleCase(lowerCaseCode)}`;

            const text =
                // prettier-ignore
                <Trans i18nKey='cart.cartSummary.voucherErrors.forApp' t={t}>
                    {/* @ts-ignore */}
                    Oops! Der Vouchercode <strong>{{ voucherCode }}</strong> kann NUR in der{' '}
                    <NavLink to={Routes.MOBILE_APP_LANDING.path}>eduki App</NavLink>eingelöst werden.
                </Trans>;

            // @ts-ignore
            setVoucherError({ text });
            showBlinder(false);
            setVoucherCode('');

            return window.scrollTo(0, 0);
        }

        saveVoucher({ code: voucherCode }, { voucherAction: 'apply-voucher' }).then(
            (response) => {
                resetCart(response);

                if (callback) {
                    callback();
                }

                const updatedCart = response.data.items.cart;
                const { originalPrice, finalPrice, totalVoucherDiscount } = updatedCart;

                qualityEvents.checkoutPage.applyCouponCode({
                    couponCode: voucherCode,
                    cartValueBefore: originalPrice,
                    cartValueAfter: finalPrice,
                    discountAmount: totalVoucherDiscount
                });
            },
            ({ data: { errors } }) => {
                const text = t('cart.cartSummary.voucherErrors.notExists');

                const rejectionReason = errors?.[0] || text;

                showBlinder(false);
                setVoucherError({ text: rejectionReason });
                setVoucherCode('');

                qualityEvents.checkoutPage.couponRejected({
                    couponCode: voucherCode,
                    rejectionReason,
                    cartTotalValue: finalPrice
                });
            }
        );

        window.scrollTo(0, 0);
    };

    const detachVoucher = () => {
        showBlinder(true);

        removeVoucher({
                voucherAction: 'cancel-voucher'
            })
            .then(resetCart, () => {
                showBlinder(false);
            });
    };

    const hasCartItems = Boolean(cart.cartItems.length);
    const shouldShowDesktopCheckoutButton = !isMobile && hasCartItems;
    const isRestrictedForSchool = cart.cartItems.some(
        (item) => schoolAccount.isAdmin(user) && item.material.hasFixedPrice
    );

    const isFinalPriceZero = formatPrice(finalPrice) === ZERO_FINAL_PRICE;
    const showDesktopPaypalButton = !isMobile && !isRestrictedForSchool;

    return (
        <div className='material-cart-summary-wrapper'>
            <div className='top-part d-none d-lg-block'>
                <div className='sum'>
                    <div className='info d-flex flex-column'>
                        <div className='tsc-h3'>{t('cart.cartSummary.sum')}</div>
                        {Boolean(!isAppliedPromoVoucher && (cart.voucher || usedCredits)) && (
                            <div className={cn('mt-24 d-flex justify-content-between align-items-center')}>
                                <div className='tsc-body'>{t('cart.cartSummary.originalPrice')}</div>
                                <div className='tsc-body'>
                                    <CurrencyPrice price={cart.originalPrice} />
                                </div>
                            </div>
                        )}
                        {Boolean(usedCredits) && (
                            <div className='mt-12 d-flex justify-content-between align-items-center'>
                                <div className='tsc-body'>{t('cart.cartSummary.credits')}</div>
                                <div className='tsc-body'>
                                    -<CurrencyPrice price={usedCredits} />
                                </div>
                            </div>
                        )}
                        {Boolean(!isAppliedPromoVoucher && cart.voucher) && (
                            <div className='mt-12 d-flex justify-content-between align-items-center'>
                                <div className='pull-left tsc-body'>
                                    <div>
                                        <div>{t('cart.cartSummary.voucherCode')}</div>
                                        <span>{cart?.voucher?.code}</span>
                                        <FontAwesomeIcon
                                            onClick={detachVoucher}
                                            className='ml-2 mb-1 cursor-pointer'
                                            height={18}
                                            name='delete'
                                            width={18}
                                        />
                                    </div>
                                </div>
                                <div className='tsc-body discount text-right'>
                                    <CurrencyPrice price={cart.totalVoucherDiscount} />
                                </div>
                            </div>
                        )}
                        {Boolean(!isAppliedPromoVoucher && (cart.voucher || usedCredits)) && <hr />}
                        <div
                            className={cn('d-flex justify-content-between align-items-center', {
                                'mt-24': isAppliedPromoVoucher || (!cart.voucher && !usedCredits)
                            })}
                        >
                            <div className='sum-desc-wrapper'>
                                <span className='tsc-body-highlighted'>{t('cart.cartSummary.sumDesc')} </span>
                                <span className='tsc-caption includesTaxText'>{t('cart.cartSummary.includesTax')}</span>
                            </div>
                            <div className='tsc-h3'>
                                <CurrencyPrice price={finalPrice} />
                            </div>
                        </div>
                        <div className='text-left'>
                            <Voucher
                                cart={cart}
                                voucherCode={voucherCode}
                                attachVoucher={attachVoucher}
                                handleVoucher={handleVoucher}
                                handleVoucherInputKeyPress={handleVoucherInputKeyPress}
                            />
                        </div>
                        {shouldShowDesktopCheckoutButton && (
                            <CartActionBlock
                                cart={cart}
                                showBlinder={showBlinder}
                                goToPaymentPage={goToPaymentPage}
                                hundredPercentVoucher={hundredPercentVoucher}
                                disabled={isRestrictedForSchool}
                            />
                        )}
                        {showDesktopPaypalButton && (
                            <div className={cn({ 'd-none': isFinalPriceZero })}>
                                <PaypalExpress onError={onPaypalExpressError} />
                            </div>
                        )}
                    </div>
                </div>

                <PaymentLogos className='d-none d-lg-flex mt-4' />

                <div className=''>
                    <div className='options'>
                        <div className='d-flex align-items-center'>
                            <FontAwesomeIcon name='lock' height={24} />
                            {t('cart.cartSummary.sslEncrypted')}
                        </div>
                        <div className='d-flex align-items-center'>
                            <FontAwesomeIcon name='check-thin' height={24} /> {t('cart.cartSummary.instantDownload')}
                        </div>
                        <div className='d-flex align-items-center'>
                            <FontAwesomeIcon name='check-thin' height={24} /> {t('cart.cartSummary.refundWarranty')}
                        </div>
                    </div>
                </div>
            </div>
            <div className='d-lg-none'>
                <CartActionBlock
                    cart={cart}
                    showBlinder={showBlinder}
                    goToPaymentPage={goToPaymentPage}
                    hundredPercentVoucher={hundredPercentVoucher}
                />
            </div>
            <div className='d-lg-none' style={{ marginTop: '50px' }}>
                <MobileVoucher
                    cart={cart}
                    voucherCode={voucherCode}
                    attachVoucher={attachVoucher}
                    detachVoucher={detachVoucher}
                    handleVoucher={handleVoucher}
                    handleVoucherInputKeyPress={handleVoucherInputKeyPress}
                />
            </div>

            {isMobile && (
                <MobileCheckoutBlock
                    goToPaymentPage={goToPaymentPage}
                    totalPrice={formatPrice(cart.finalPrice)}
                    hasCartItems={hasCartItems}
                >
                    <div className={cn({ 'd-none': isFinalPriceZero })}>
                        <PaypalExpress onError={onPaypalExpressError} />
                    </div>
                </MobileCheckoutBlock>
            )}

            <PaymentLogos className='d-lg-none' />
        </div>
    );
});

const translate = translation('cart');

export default translate(MaterialCartSummary);
